import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useDragScroll } from '../hooks/useDragScroll';
import { groupColor, initials, daysUntilNum, shortLocation, formatCategories } from '../components/TournamentCard';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';


function surfaceToPhoto(surface) {
  if (surface === 'beach') return 'photo-beach';
  if (surface === 'green') return 'photo-sunset';
  return 'photo-court';
}

function SearchTournamentCard({ item, navigate }) {
  const t = item.tournament;
  const photoClass = surfaceToPhoto(t.surface);
  const days = daysUntilNum(t.date);
  const dStr = t.date
    ? new Date(t.date + 'T12:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }).toUpperCase()
    : '';
  const cats = formatCategories(t);

  return (
    <div className="tourney-hero sand-grain on-hero"
      onClick={() => navigate(`/tournaments/${t.id}`)}
      style={{ borderRadius: 16, marginBottom: 12, cursor: 'pointer', minHeight: 130 }}>
      <div className={`hero-photo ${photoClass}`} />
      <div className="hero-tint" />
      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 130 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {cats.map(c => (
              <span key={c} className="chip" style={{ fontSize: 11, padding: '2px 8px' }}>{c}</span>
            ))}
          </div>
          <span className="mono" style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11 }}>{dStr}</span>
        </div>
        <div>
          <div className="display" style={{ fontSize: 22, lineHeight: 1.2, marginTop: 8, marginBottom: 6 }}>{t.name}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div className="mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>
              {shortLocation(t.location)}{item.distanceKm ? ` · ${Math.round(item.distanceKm)} km` : ''}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {item.teamCount !== undefined && !item.isFull && (
                <span className="mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>
                  {item.teamCount} équipe{item.teamCount !== 1 ? 's' : ''}
                </span>
              )}
              {item.isFull && (
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20,
                  background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)' }}>
                  COMPLET
                </span>
              )}
            </div>
          </div>
          {days >= 0 && days <= 7 && (
            <div style={{ marginTop: 6 }}>
              <span className="chip chip-sun" style={{ fontSize: 10, padding: '2px 8px' }}>
                {days <= 0 ? "Aujourd'hui" : days === 1 ? 'Demain' : `Dans ${days}j`}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const DATE_GROUPS = [
  { key: 'weekend', label: 'Ce week-end' },
  { key: 'week',    label: 'Cette semaine' },
  { key: 'month',   label: 'Ce mois' },
  { key: 'later',   label: 'Plus tard' },
];

const AV_GRADIENTS = [
  'linear-gradient(135deg,#F5A623,#DD8A0A)',
  'linear-gradient(135deg,#0A8FA8,#066D81)',
  'linear-gradient(135deg,#E5503E,#C23B2A)',
  'linear-gradient(135deg,#6BAA1E,#4E8012)',
  'linear-gradient(135deg,#9B59B6,#7D3C98)',
  'linear-gradient(135deg,#3498DB,#1A6EA8)',
];
function memberGrad(id) {
  let h = 0;
  for (const c of String(id)) h = (h * 31 + c.charCodeAt(0)) & 0xFFFFFF;
  return AV_GRADIENTS[Math.abs(h) % AV_GRADIENTS.length];
}


function getDateGroup(dateStr) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + 'T12:00:00');
  const diff = Math.round((d - today) / 86400000);
  if (diff < 0) return null;
  const daysToSat = (6 - today.getDay() + 7) % 7;
  if (diff === daysToSat || diff === daysToSat + 1) return 'weekend';
  if (diff < 7) return 'week';
  if (d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear()) return 'month';
  return 'later';
}

const LEVEL_BADGES = { loisir: 'Loisir', departemental: 'Dép.', regional: 'Régio.', national: 'Nat.', pro: 'Pro' };
const CATEGORY_LEVELS = ['Loisir', 'Départemental', 'Régional', 'N3', 'N2', 'N1', 'Élite'];

function PartnerSearchCard({ ps, onContact }) {
  const nav = useNavigate();
  const u = ps.userDetails || {};
  const grad = memberGrad(u.id || ps.userId || ps.id);
  const ini = initials(u.firstName, u.lastName);
  const lvl = LEVEL_BADGES[u.level];
  const pos = ps.position ? ps.position.charAt(0).toUpperCase() + ps.position.slice(1) : null;
  const minLvl = ps.minLevel ? LEVEL_BADGES[ps.minLevel] : null;
  const dStr = ps.tournamentDate
    ? new Date(ps.tournamentDate + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
    : null;

  const diffMs = ps.createdAt ? Date.now() - new Date(ps.createdAt) : 0;
  const ago = diffMs < 3600000 ? `${Math.max(1, Math.floor(diffMs / 60000))} min`
            : diffMs < 86400000 ? `${Math.floor(diffMs / 3600000)} h`
            : `${Math.floor(diffMs / 86400000)} j`;

  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
          background: u.avatarUrl ? 'transparent' : grad,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', color: '#fff', fontSize: 15, fontWeight: 700,
          cursor: 'pointer',
        }} onClick={() => u.id && nav(`/profil/${u.id}`)}>
          {u.avatarUrl
            ? <img src={u.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : ini}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{u.firstName} {u.lastName}</div>
            <span className="mono" style={{ fontSize: 10, color: 'var(--text-4)' }}>{ago}</span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
            {lvl && <span className="chip chip-sun" style={{ padding: '2px 8px', fontSize: 11 }}>{lvl}</span>}
            {pos && <span className="chip" style={{ padding: '2px 8px', fontSize: 11 }}>{pos}</span>}
            {minLvl && <span className="chip" style={{ padding: '2px 8px', fontSize: 11 }}>Min. {minLvl}</span>}
          </div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: ps.message ? 6 : 0 }}>
            {[ps.tournamentName, ps.playerFormat, dStr].filter(Boolean).join(' · ')}
          </div>
          {ps.message && (
            <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.4, fontStyle: 'italic' }}>
              "{ps.message}"
            </div>
          )}
        </div>
      </div>
      {onContact && (
        <div style={{ marginTop: 12 }}>
          <button onClick={() => onContact(ps)} className="btn btn-ghost btn-sm"
            style={{ width: '100%', padding: '9px 0', fontSize: 13, fontWeight: 600 }}>
            Contacter
          </button>
        </div>
      )}
    </div>
  );
}

function OpenTeamCard({ team, onJoin }) {
  const grad = memberGrad(team.id);
  const ini = team.name ? team.name.substring(0, 2).toUpperCase() : '??';
  const dStr = team.tournamentDate
    ? new Date(team.tournamentDate + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
    : null;
  const spots = team.slotsLeft || 0;
  return (
    <div className="card team-card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: grad,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 14, fontWeight: 800,
        }}>{ini}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {team.name}
          </div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>
            {[team.tournamentName, team.playerFormat, dStr].filter(Boolean).join(' · ')}
          </div>
        </div>
        <span className={`chip team-spots${spots === 0 ? ' full' : ''}`}
          style={{ flexShrink: 0, padding: '4px 10px', fontSize: 12, fontWeight: 700 }}>
          {spots > 0 ? `${spots} place${spots > 1 ? 's' : ''} restante${spots > 1 ? 's' : ''}` : 'Complet'}
        </span>
      </div>
      {spots > 0 && onJoin && (
        <div style={{ marginTop: 12 }}>
          <button onClick={() => onJoin(team)} className="btn btn-ghost btn-sm"
            style={{ width: '100%', padding: '9px 0', fontSize: 13, fontWeight: 600 }}>
            Rejoindre
          </button>
        </div>
      )}
    </div>
  );
}

function TournamentSearch({ user, onLogout, chatUnread = 0 }) {
  const navigate = useNavigate();
  const dragChips = useDragScroll();
  const [searchMode, setSearchMode] = useState('tournois'); // 'tournois' | 'equipes'
  const [query, setQuery]           = useState('');
  const [results, setResults]       = useState([]);
  const [partnerSearches, setPartnerSearches] = useState([]);
  const [openTeams, setOpenTeams]   = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [inviteModal, setInviteModal]     = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const [formatFilter, setFormatFilter]   = useState('');
  const [genderFilter, setGenderFilter]   = useState('');
  const [levelFilter, setLevelFilter]     = useState('');
  const [dateFilter, setDateFilter]       = useState('');
  const [surfaceFilter, setSurfaceFilter] = useState('');

  const [filtersOpen, setFiltersOpen] = useState(false);

  // Géolocalisation (persistée en session)
  const [userLat, setUserLat]   = useState(() => { const v = sessionStorage.getItem('geo_lat');    return v ? parseFloat(v) : null; });
  const [userLng, setUserLng]   = useState(() => { const v = sessionStorage.getItem('geo_lng');    return v ? parseFloat(v) : null; });
  const [radius, setRadius]     = useState(() => { const v = sessionStorage.getItem('geo_radius'); return v ? parseInt(v, 10) : 25; });
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState('');

  // Toast de confirmation
  const [toast, setToast] = useState(null);

  const [groups, setGroups] = useState([]);
  const token = localStorage.getItem('token');

  useEffect(() => {
    axios.get(`${API_URL}/api/groups`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setGroups(res.data))
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (userLat && userLng) {
      sessionStorage.setItem('geo_lat', userLat);
      sessionStorage.setItem('geo_lng', userLng);
      sessionStorage.setItem('geo_radius', radius);
    }
  }, [userLat, userLng, radius]);

  const handleGeolocate = () => {
    if (!navigator.geolocation) { setGeoError('Géolocalisation non supportée'); return; }
    setGeoLoading(true); setGeoError('');
    navigator.geolocation.getCurrentPosition(
      pos => { setUserLat(pos.coords.latitude); setUserLng(pos.coords.longitude); setGeoLoading(false); },
      () => { setGeoError('Localisation refusée ou indisponible'); setGeoLoading(false); }
    );
  };

  const clearGeo = () => {
    setUserLat(null); setUserLng(null); setGeoError('');
    sessionStorage.removeItem('geo_lat');
    sessionStorage.removeItem('geo_lng');
    sessionStorage.removeItem('geo_radius');
  };

  const search = useCallback(async () => {
    setLoading(true); setError(''); setHasSearched(true);
    try {
      const params = new URLSearchParams();
      if (query)         params.set('q', query);
      if (formatFilter)  params.set('format', formatFilter);
      if (genderFilter)  params.set('gender', genderFilter);
      if (levelFilter)   params.set('level', levelFilter);
      if (dateFilter)    params.set('date', dateFilter);
      if (surfaceFilter) params.set('surface', surfaceFilter);
      if (userLat && userLng) {
        params.set('lat', userLat);
        params.set('lng', userLng);
        params.set('radius', radius);
      }
      const res = await axios.get(`${API_URL}/api/tournaments/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(res.data);
    } catch {
      setError('Erreur lors de la recherche.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [query, formatFilter, genderFilter, levelFilter, dateFilter, surfaceFilter, userLat, userLng, radius, token]);

  useEffect(() => {
    if (searchMode !== 'tournois') return;
    const t = setTimeout(() => search(), 400);
    return () => clearTimeout(t);
  }, [query, formatFilter, genderFilter, levelFilter, dateFilter, surfaceFilter, userLat, userLng, radius, searchMode]); // eslint-disable-line

  useEffect(() => {
    if (searchMode !== 'equipes') return;
    setTeamLoading(true);
    const params = new URLSearchParams({ limit: '20' });
    if (query) params.set('q', query);
    Promise.all([
      axios.get(`${API_URL}/api/partner-searches?${params}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setPartnerSearches(r.data)).catch(() => setPartnerSearches([])),
      axios.get(`${API_URL}/api/teams/open?limit=15`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setOpenTeams(r.data)).catch(() => setOpenTeams([])),
    ]).finally(() => setTeamLoading(false));
  }, [searchMode, query, token]); // eslint-disable-line

  const toggle = (setter) => (val) => setter(v => v === val ? '' : val);
  const toggleFormat  = toggle(setFormatFilter);
  const toggleGender  = toggle(setGenderFilter);
  const toggleLevel   = toggle(setLevelFilter);
  const toggleDate    = toggle(setDateFilter);
  const toggleSurface = toggle(setSurfaceFilter);

  const clearAll = () => {
    setQuery(''); setFormatFilter(''); setGenderFilter(''); setLevelFilter('');
    setDateFilter(''); setSurfaceFilter(''); clearGeo();
  };

  const activeCount = [formatFilter, genderFilter, levelFilter, dateFilter, surfaceFilter, userLat ? 'geo' : '']
    .filter(Boolean).length;

  const activeChips = [
    formatFilter  && { key: 'format',  label: formatFilter,                                                                                      clear: () => setFormatFilter('') },
    genderFilter  && { key: 'gender',  label: genderFilter === 'mix' ? 'Mixte' : genderFilter === 'masculin' ? 'Masculin' : 'Féminin',           clear: () => setGenderFilter('') },
    levelFilter   && { key: 'level',   label: levelFilter,                                                                                       clear: () => setLevelFilter('') },
    surfaceFilter && { key: 'surface', label: surfaceFilter === 'beach' ? '🏖️ Beach' : surfaceFilter === 'green' ? '🌿 Green' : '🏛️ Gymnase',  clear: () => setSurfaceFilter('') },
    dateFilter    && { key: 'date',    label: dateFilter === 'weekend' ? 'Ce week-end' : 'Ce mois',                                              clear: () => setDateFilter('') },
    userLat       && { key: 'geo',     label: `📍 ${radius} km`,                                                                                  clear: clearGeo },
  ].filter(Boolean);

  return (
    <>
      {/* Header Spike */}
      <div style={{ padding: '16px 20px 12px', flexShrink: 0 }}>
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="display" style={{ fontSize: 26 }}>Trouver</div>
          {searchMode === 'tournois' ? (
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/creer')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="M12 5v14M5 12h14"/>
              </svg>
              Ajouter un tournoi
            </button>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/creer/partenaire')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="M12 5v14M5 12h14"/>
              </svg>
              Créer une recherche
            </button>
          )}
        </div>

        {/* Barre de recherche Spike */}
        <div className="row gap-2" style={{
          padding: '10px 14px', borderRadius: 'var(--r-md)',
          background: 'var(--ink-2)', border: '1px solid var(--line)',
        }}>
          <span style={{ color: 'var(--text-3)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="6.5"/><path d="M16 16l4.5 4.5"/>
            </svg>
          </span>
          <input value={query} onChange={e => setQuery(e.target.value)}
            placeholder={searchMode === 'equipes' ? 'Niveau, ville, nom…' : 'Lieu, format, date...'}
            style={{ flex: 1, background: 'transparent', border: 0, outline: 'none', color: 'var(--text)', fontSize: 14, fontFamily: 'inherit' }} />
          {searchMode === 'tournois' && (
            <button onClick={() => setFiltersOpen(v => !v)} style={{
              padding: '6px 10px', borderRadius: 8, background: activeCount > 0 ? 'var(--text)' : 'var(--ink-3)',
              color: activeCount > 0 ? 'var(--sun)' : 'var(--text)', fontSize: 12, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 6h16M7 12h10M10 18h4"/>
              </svg>
              Filtres{activeCount > 0 ? ` · ${activeCount}` : ''}
            </button>
          )}
        </div>

        {/* Active filter chips */}
        {searchMode === 'tournois' && activeChips.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {activeChips.map(chip => (
              <span key={chip.key} className="chip chip-sun" style={{ cursor: 'pointer' }} onClick={chip.clear}>
                {chip.label} ✕
              </span>
            ))}
            <span className="chip" style={{ cursor: 'pointer' }} onClick={clearAll}>Tout effacer</span>
          </div>
        )}

        {/* Tabs Tournois / Équipes */}
        <div className="row" style={{ marginTop: 14, gap: 4, borderBottom: '1px solid var(--line)' }}>
          {[['tournois', 'Tournois'], ['equipes', 'Équipes']].map(([m, lbl]) => (
            <button key={m} onClick={() => { setSearchMode(m); setQuery(''); }} style={{
              padding: '10px 14px', position: 'relative', fontSize: 14, fontWeight: 600,
              color: searchMode === m ? 'var(--text)' : 'var(--text-3)',
              background: 'transparent', border: 'none',
            }}>
              {lbl}
              {searchMode === m && <span style={{
                position: 'absolute', bottom: -1, left: 14, right: 14, height: 2,
                background: 'var(--sun)', borderRadius: 2,
              }} />}
            </button>
          ))}
        </div>

        {/* Quick filter chips — Tournois only */}
        {searchMode === 'tournois' && (
          <div ref={dragChips.ref} onMouseDown={dragChips.onMouseDown} onMouseMove={dragChips.onMouseMove} onMouseUp={dragChips.onMouseUp} onMouseLeave={dragChips.onMouseLeave} style={{ display: 'flex', gap: 8, marginTop: 12, overflowX: 'auto', paddingBottom: 2 }}>
            {[
              { key: 'tous',     label: 'Tous',         active: !dateFilter && !formatFilter && !userLat },
              { key: 'weekend',  label: 'Ce week-end',  active: dateFilter === 'weekend' },
              { key: 'geo20',    label: '< 20 km',      active: !!userLat && radius <= 20 },
              { key: '2x2',      label: '2x2',          active: formatFilter === '2x2' },
              { key: '4x4',      label: '4x4',          active: formatFilter === '4x4' },
            ].map(chip => (
              <button key={chip.key} onClick={() => {
                if (chip.key === 'tous') { clearAll(); }
                else if (chip.key === 'weekend') { setDateFilter(v => v === 'weekend' ? '' : 'weekend'); }
                else if (chip.key === '2x2') { setFormatFilter(v => v === '2x2' ? '' : '2x2'); }
                else if (chip.key === '4x4') { setFormatFilter(v => v === '4x4' ? '' : '4x4'); }
                else if (chip.key === 'geo20') {
                  if (userLat) { clearGeo(); }
                  else { setRadius(20); handleGeolocate(); }
                }
              }} style={{
                flexShrink: 0,
                padding: '7px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                background: chip.active ? 'var(--text)' : 'var(--ink-3)',
                color: chip.active ? 'var(--ink)' : 'var(--text-2)',
                border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
              }}>
                {chip.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="page-content">

        {/* Onglet Équipes */}
        {searchMode === 'equipes' && (
          <>
            {teamLoading && (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-3)' }}>
                <span className="mono">Chargement…</span>
              </div>
            )}

            {!teamLoading && (
              <>
                {/* ÉQUIPES À REJOINDRE */}
                {openTeams.length > 0 && (
                  <div style={{ padding: '16px 20px 0' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '0.09em', color: 'var(--text-3)', marginBottom: 10 }}>
                      Équipes à rejoindre · {openTeams.length}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {openTeams.map(team => (
                        <OpenTeamCard key={team.id} team={team}
                          onJoin={t => navigate(`/tournaments/${t.tournamentId}`)} />
                      ))}
                    </div>
                  </div>
                )}

                {/* RECHERCHES DE JOUEURS */}
                <div style={{ padding: `${openTeams.length > 0 ? 20 : 16}px 20px 0` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.09em', color: 'var(--text-3)', marginBottom: 10 }}>
                    Recherches de joueurs{partnerSearches.length > 0 ? ` · ${partnerSearches.length}` : ''}
                  </div>
                  {partnerSearches.length === 0 ? (
                    <div style={{ padding: '24px 0', textAlign: 'center' }}>
                      <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
                        Aucune recherche de partenaire active.
                      </div>
                      <button onClick={() => navigate('/creer/partenaire')}
                        className="btn btn-ghost btn-sm" style={{ marginTop: 12 }}>
                        Créer une recherche
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {partnerSearches.map(ps => (
                        <PartnerSearchCard key={ps.id} ps={ps}
                          onContact={ps.userId !== user?.id ? (p) => navigate(`/profil/${p.userId}`) : null} />
                      ))}
                    </div>
                  )}
                </div>

                {openTeams.length === 0 && partnerSearches.length === 0 && (
                  <div style={{ padding: '48px 20px', textAlign: 'center' }}>
                    <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 16 }}>
                      Aucune équipe ni recherche de partenaire disponible.
                    </div>
                    <button onClick={() => navigate('/creer/partenaire')}
                      className="btn btn-primary btn-sm">
                      Créer une recherche
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Résultats tournois */}
        {searchMode === 'tournois' && (
          <>
            {loading && (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-3)' }}>
                <span className="mono">Recherche…</span>
              </div>
            )}

        {error && (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🚧</div>
            <div className="mono" style={{ color: 'var(--text-3)' }}>{error}</div>
          </div>
        )}

        {!loading && !error && hasSearched && (() => {
          const grouped = DATE_GROUPS
            .map(g => ({ ...g, items: results.filter(item => getDateGroup(item.tournament.date) === g.key) }))
            .filter(g => g.items.length > 0);

          return (
            <>
              <div style={{ padding: '12px 20px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', color: 'var(--text-3)' }}>
                  {results.length} tournoi{results.length !== 1 ? 's' : ''}{userLat ? ` près de toi` : ''}
                </span>
              </div>

              {results.length === 0 && (
                <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>Aucun tournoi trouvé</div>
                  <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Essayez d'autres critères ou un rayon plus grand.</div>
                </div>
              )}

              {grouped.map(group => (
                <div key={group.key}>
                  <div style={{ padding: '6px 20px 4px' }}>
                    <span className="mono" style={{ fontSize: 10, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                      {group.label} · {group.items.length}
                    </span>
                  </div>
                  <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column' }}>
                    {group.items.map(item => (
                      <SearchTournamentCard key={item.tournament.id}
                        item={item}
                        navigate={navigate}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </>
          );
        })()}
          </>
        )}
      </div>

      {/* Tiroir de filtres — Spike bottom sheet */}
      {filtersOpen && (
        <div onClick={() => setFiltersOpen(false)} style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'flex-start',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            width: '100%', maxHeight: '92%',
            background: 'var(--ink)', borderRadius: '0 0 24px 24px',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 20px', borderBottom: '1px solid var(--line)', flexShrink: 0 }}>
              <button onClick={() => { clearAll(); setFiltersOpen(false); }}
                style={{ color: 'var(--text-3)', fontSize: 13, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>
                Réinitialiser
              </button>
              <div className="display" style={{ fontSize: 17 }}>Filtres</div>
              <button onClick={() => setFiltersOpen(false)}
                style={{ color: 'var(--text-3)', fontSize: 13, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>
                Fermer
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 20px 4px' }}>
              {/* Format */}
              <div style={{ padding: '12px 0', borderBottom: '1px solid var(--line)' }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Format</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['2x2','3x3','4x4','6x6'].map(f => (
                    <button key={f} onClick={() => toggleFormat(f)} style={{
                      padding: '8px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                      background: formatFilter === f ? 'var(--sun)' : 'var(--ink-3)',
                      color: formatFilter === f ? 'var(--ink)' : 'var(--text-2)',
                    }}>{f}</button>
                  ))}
                </div>
              </div>

              {/* Catégorie */}
              <div style={{ padding: '12px 0', borderBottom: '1px solid var(--line)' }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Catégorie</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[['mix','Mixte'],['masculin','Masculin'],['feminin','Féminin']].map(([val, lbl]) => (
                    <button key={val} onClick={() => toggleGender(val)} style={{
                      flex: 1, padding: '8px 0', borderRadius: 999, fontSize: 13, fontWeight: 600,
                      background: genderFilter === val ? 'var(--sun)' : 'var(--ink-3)',
                      color: genderFilter === val ? 'var(--ink)' : 'var(--text-2)',
                    }}>{lbl}</button>
                  ))}
                </div>
              </div>

              {/* Niveau */}
              <div style={{ padding: '12px 0', borderBottom: '1px solid var(--line)' }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Niveau</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {CATEGORY_LEVELS.map(lv => (
                    <button key={lv} onClick={() => toggleLevel(lv)} style={{
                      padding: '8px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                      background: levelFilter === lv ? 'var(--sun)' : 'var(--ink-3)',
                      color: levelFilter === lv ? 'var(--ink)' : 'var(--text-2)',
                    }}>{lv}</button>
                  ))}
                </div>
              </div>

              {/* Surface */}
              <div style={{ padding: '12px 0', borderBottom: '1px solid var(--line)' }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Surface</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[['beach','Beach'],['green','Green'],['gymnase','Gymnase']].map(([val, lbl]) => (
                    <button key={val} onClick={() => toggleSurface(val)} style={{
                      flex: 1, padding: '8px 0', borderRadius: 999, fontSize: 13, fontWeight: 600,
                      background: surfaceFilter === val ? 'var(--sun)' : 'var(--ink-3)',
                      color: surfaceFilter === val ? 'var(--ink)' : 'var(--text-2)',
                    }}>{lbl}</button>
                  ))}
                </div>
              </div>

              {/* Date */}
              <div style={{ padding: '12px 0', borderBottom: '1px solid var(--line)' }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Date</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[['weekend','Ce week-end'],['month','Ce mois']].map(([val, lbl]) => (
                    <button key={val} onClick={() => toggleDate(val)} style={{
                      flex: 1, padding: '8px 0', borderRadius: 999, fontSize: 13, fontWeight: 600,
                      background: dateFilter === val ? 'var(--sun)' : 'var(--ink-3)',
                      color: dateFilter === val ? 'var(--ink)' : 'var(--text-2)',
                    }}>{lbl}</button>
                  ))}
                </div>
              </div>

              {/* Localisation */}
              <div style={{ padding: '12px 0' }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Localisation</div>
                {!userLat ? (
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8, lineHeight: 1.4 }}>
                      Active ta position pour trouver les tournois autour de chez toi.
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button onClick={handleGeolocate} disabled={geoLoading} className="btn btn-ghost btn-sm">
                        {geoLoading ? 'Localisation…' : '📍 Activer ma position'}
                      </button>
                      {geoError && <span style={{ fontSize: 11, color: '#E5503E' }}>{geoError}</span>}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <span className="chip chip-lime">📍 Position activée</span>
                      <button onClick={clearGeo} style={{ fontSize: 11, color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>✕ Retirer</button>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[10, 25, 50, 100].map(r => (
                        <button key={r} onClick={() => setRadius(r)} style={{
                          flex: 1, padding: '8px 0', borderRadius: 999, fontSize: 13, fontWeight: 600,
                          background: radius === r ? 'var(--sun)' : 'var(--ink-3)',
                          color: radius === r ? 'var(--ink)' : 'var(--text-2)',
                        }}>{r} km</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ height: 8 }} />
            </div>

            <div style={{ padding: '12px 20px 14px', borderTop: '1px solid var(--line)', flexShrink: 0 }}>
              <button onClick={() => setFiltersOpen(false)} className="btn btn-primary" style={{ width: '100%', padding: '14px 20px' }}>
                Appliquer{activeCount > 0 ? ` (${activeCount})` : ''}
              </button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 10px', flexShrink: 0 }}>
              <span style={{ width: 38, height: 4, borderRadius: 2, background: 'var(--ink-4)' }} />
            </div>
          </div>
        </div>
      )}

      {/* Modal — Inviter un membre dans un groupe */}
      {inviteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 300, display: 'flex', alignItems: 'flex-end', backdropFilter: 'blur(4px)' }}
          onClick={() => setInviteModal(null)}>
          <div style={{ background: 'var(--ink)', borderRadius: '20px 20px 0 0', padding: '20px 20px 36px', width: '100%', maxHeight: '70vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ width: 40, height: 4, background: 'var(--ink-4)', borderRadius: 4, margin: '0 auto 20px' }} />
            <div className="display" style={{ fontSize: 20, marginBottom: 4 }}>Inviter {inviteModal.firstName}</div>
            <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 16 }}>Partage un lien d'invitation depuis un de tes groupes</div>
            {groups.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-3)', textAlign: 'center', padding: '12px 0' }}>
                Vous n'avez pas encore de groupe.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {groups.map(g => {
                  const link = `${window.location.origin}/rejoindre/${g.inviteCode}`;
                  return (
                    <div key={g.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: groupColor(g.id), flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{g.name}</div>
                        <div className="mono" style={{ color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link}</div>
                      </div>
                      <button onClick={() => {
                        navigator.clipboard.writeText(link).then(() => {
                          setToast('Lien copié !');
                          setTimeout(() => setToast(null), 2500);
                          setInviteModal(null);
                        });
                      }} className="btn btn-primary btn-sm">
                        Copier
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 96, left: '50%', transform: 'translateX(-50%)',
          zIndex: 400, padding: '10px 20px', borderRadius: 999,
          background: 'var(--text)', color: 'var(--ink)',
          fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        }}>{toast}</div>
      )}
    </>
  );
}

export default TournamentSearch;
